import React from 'react';
import type { FC, ReactNode } from 'react';
import { Button } from '../../ui/Button/Button';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PositionDetails } from '../PositionDetails/PositionDetails';
import type { AnalogsLogistics } from '../PositionDetails/PositionDetails';
import { PositionRow } from '../PositionRow/PositionRow';
import { IconDownload, IconSearch, IconSpinner, IconUpload } from '../../../assets/icons/icons';
import type { Position } from '../../../types/project';
import { isSearchStarted } from '../../../entities/project/model/position';
import type { ReportDelivery } from '../../../entities/project/model/types';
import { formatNumber, formatRub, pluralWithCount } from '../../../utils/format';
import styles from './PositionsPanel.module.scss';
import { TableFooter } from '../../ui/TableFooter/TableFooter';
import { Tooltip } from '../../ui/Tooltip/Tooltip';
import type { ScreenshotsState } from '../../../entities/project/model/screenshots';
import { ScreenshotsButton } from '../../ui/ScreenshotsButton/ScreenshotsButton';
import { DEFAULT_PAGE_SIZE } from '../../../shared/config/pagination';
import { BulkSwitch, type BulkState } from '../../ui/BulkSwitch/BulkSwitch';

const TABLE_COLUMNS = 13;

interface PositionsPanelProps {
    reportId: string;
    dirtyIds: Set<string>;
    bulkDirtyIds: Set<string>;
    onRestartPosition: (id: string) => void;
    onDiscardPosition: (id: string) => void;
    onOfferDeleted?: (positionId: string) => void;
    onRestartDirty: () => void;
    onDiscardDirty: () => void;
    restartingDirty?: boolean;
    onShowDimensions: (id: string) => void;
    positions: Position[];
    logisticsCount: number;
    deliveryAddress: string;
    pendingCount: number;
    searchStarting: boolean;
    page: number;
    pageCount: number;
    total: number;
    deliveryAvgTotal?: number;
    isPageLoading: boolean;
    onPageChange: (value: number) => void;
    onPickFile: () => void;
    onAddManually: () => void;
    projectRegion: string;
    onChangePosition: (id: string, patch: Partial<Position>) => void;
    onEditPosition: (position: Position) => void;
    onDeletePosition: (position: Position) => void;
    onRunSearch: () => void;
    limitLeft: number | null;
    onRestartSearch: () => void;
    delivery?: ReportDelivery;
    isSearching: boolean;
    stopping?: boolean;
    actionBusy?: boolean;
    onStopSearch: () => void;
    onDownload: (withLogistics: boolean) => void;
    isCompleted: boolean;
    screenshots: ScreenshotsState;
    screenshotsBusy: boolean;
    onScreenshots: () => void;
    branchesBusy?: boolean;
    onClearAll: () => void;
    onTogglePosition: (id: string, key: 'analogs' | 'logistics') => void;
    onToggleAll: (key: 'analogs' | 'logistics', value: boolean) => void;
}

export const PositionsPanel: FC<PositionsPanelProps> = ({
    reportId,
    positions,
    logisticsCount,
    deliveryAddress,
    pendingCount,
    searchStarting,
    page,
    pageCount,
    total,
    deliveryAvgTotal = 0,
    isPageLoading,
    onPageChange,
    onPickFile,
    onAddManually,
    projectRegion,
    dirtyIds,
    bulkDirtyIds,
    onRestartPosition,
    onDiscardPosition,
    onOfferDeleted,
    onRestartDirty,
    onDiscardDirty,
    restartingDirty = false,
    onShowDimensions,
    onChangePosition,
    onEditPosition,
    onDeletePosition,
    onRunSearch,
    limitLeft,
    onRestartSearch,
    delivery,
    isSearching,
    stopping = false,
    actionBusy = false,
    onStopSearch,
    onDownload,
    isCompleted,
    screenshots,
    screenshotsBusy,
    onScreenshots,
    branchesBusy,
    onClearAll,
    onTogglePosition,
    onToggleAll,
}) => {
    const [openRows, setOpenRows] = useState<Set<string>>(new Set());
    const [analogsLogistics, setAnalogsLogistics] = useState<Record<string, AnalogsLogistics>>({});

    const bulkState = useCallback(
        (key: 'analogs' | 'logistics'): BulkState => {
            if (!positions.length) return 'off';
            const onCount = positions.filter((item) => item[key]).length;
            if (onCount === 0) return 'off';
            return onCount === positions.length ? 'on' : 'mixed';
        },
        [positions],
    );

    const analogsState = bulkState('analogs');
    const logisticsState = bulkState('logistics');

    const bulkDisabled =
        isSearching || searchStarting || actionBusy || restartingDirty || positions.length === 0;

    /* Кнопки «Отменить изменения / Перезапустить изменённые» в подвале — при любом изменении
       позиций, в том числе на других страницах. Плашка в строке при массовом изменении скрыта. */
    const dirtyCount = dirtyIds.size;
    const showDirtyActions = dirtyCount > 0 && !isSearching;

    const handleAnalogsLogistics = useCallback((id: string, status: AnalogsLogistics) => {
        setAnalogsLogistics((prev) => (prev[id] === status ? prev : { ...prev, [id]: status }));
    }, []);

    const perPage = DEFAULT_PAGE_SIZE;

    const safePage = Math.min(page, pageCount);

    const bodyRef = useRef<HTMLDivElement>(null);

    const goToPage = (next: number) => {
        onPageChange(next);
        bodyRef.current?.scrollTo({ top: 0 });
    };

    const visible = useMemo(
        () => positions,
        [positions],
    );

    useEffect(() => {
        if (page > pageCount) onPageChange(pageCount);
    }, [onPageChange, page, pageCount]);

    const openable = visible.filter(
        (item) => isSearchStarted(item) && Boolean(item.avgFoundPrice),
    );

    const allOpen = openable.length > 0 && openable.every((item) => openRows.has(item.id));

    const toggleAllRows = () =>
        setOpenRows(allOpen ? new Set() : new Set(openable.map((item) => item.id)));

    const toggleRow = (position: Position) => {
        if (!isSearchStarted(position)) return;
        setOpenRows((prev) => {
            const next = new Set(prev);
            if (next.has(position.id)) next.delete(position.id);
            else next.add(position.id);
            return next;
        });
    };

    const originalIndex = (position: Position): number => {
        const same = positions.findIndex(
            (item) =>
                !item.isDuplicate &&
                item.name.trim().toLowerCase() === position.name.trim().toLowerCase() &&
                (item.spec ?? '').trim() === (position.spec ?? '').trim() &&
                (item.region ?? '') === (position.region ?? ''),
        );

        return same === -1 ? 0 : same + 1;
    };

    const goodsTotal = delivery?.goods ?? 0;

    const deliveryTotal = delivery?.total ?? 0;
    const deliveryPending = Boolean(delivery?.isProcessing);
    const withoutOffers = delivery?.withoutOffers ?? 0;

    const deliveryShown = deliveryAvgTotal || delivery?.minTotal || deliveryTotal;
    const hasLogistics =
        logisticsCount > 0 || deliveryTotal > 0 || deliveryAvgTotal > 0 || deliveryPending;

    const hasResults = goodsTotal > 0;
    const showTotal = hasResults && isCompleted;

    const missingAddress = logisticsCount > 0 && !deliveryAddress;

    const notEnoughLimit = limitLeft !== null && pendingCount > limitLeft;

    const canRunSearch =
        pendingCount > 0 && !searchStarting && !missingAddress && !notEnoughLimit;

    const showsRunLabel = !isSearching && !searchStarting && !(hasResults && !canRunSearch);

    const restartBlocked = branchesBusy || stopping;
    const isRestart = !canRunSearch;

    /* Пока есть изменённые позиции, главное действие — «Перезапустить изменённые»,
       а «Искать заново» (перезапуск всего проекта) становится второстепенной кнопкой. */
    const mainIsPrimary = !(showDirtyActions && isRestart);

    let footNote: ReactNode = null;

    if (notEnoughLimit) {
        footNote = (
            <>
                <b className={styles.footWarn}>
                    {limitLeft === 0 ? 'Лимит запросов исчерпан' : `Осталось запросов: ${limitLeft}`}
                </b>{' '}
                — позиций к обработке {pendingCount}
            </>
        );
    } else if (missingAddress) {
        footNote = (
            <>
                <b className={styles.footWarn}>Не указан адрес доставки</b> — логистика по заявке не
                рассчитается
            </>
        );
    } else if (pendingCount > 0) {
        footNote = `К обработке ${pluralWithCount(pendingCount, 'позиция', 'позиции', 'позиций')}${logisticsCount ? `, с логистикой ${logisticsCount}` : ''
            }`;
    }

    return (
        <section className={styles.panel}>
            <div className={styles.head}>
                <h2>Позиции проекта</h2>
                <span className={styles.countTag}>{formatNumber(total + pendingCount)}</span>

                <div className={styles.headRight}>
                    <Tooltip text="Искать аналоги для всех позиций" align="right">
                        <BulkSwitch
                            label="Аналоги"
                            state={analogsState}
                            disabled={bulkDisabled}
                            onChange={(value) => onToggleAll('analogs', value)}
                        />
                    </Tooltip>
                    <Tooltip text="Рассчитать доставку для всех позиций" align="right">
                        <BulkSwitch
                            label="Логистика"
                            state={logisticsState}
                            disabled={bulkDisabled}
                            onChange={(value) => onToggleAll('logistics', value)}
                        />
                    </Tooltip>

                    <span className={styles.headDivider} />

                    <Button size="sm" icon={<IconUpload />} onClick={onPickFile}>
                        Загрузить файл
                    </Button>
                    <Button size="sm" onClick={onAddManually}>
                        Добавить вручную
                    </Button>
                    <Tooltip text={screenshots.hint} align="right">
                        <ScreenshotsButton
                            state={screenshots}
                            busy={screenshotsBusy}
                            onClick={onScreenshots}
                        />
                    </Tooltip>

                    {openable.length > 0 && (
                        <button
                            type="button"
                            className={styles.headLink}
                            onClick={toggleAllRows}
                        >
                            {allOpen ? 'Скрыть все' : 'Раскрыть все'}
                        </button>
                    )}
                </div>
            </div>

            <div className={styles.body} ref={bodyRef}>
                {isPageLoading && (
                    <div className={styles.pageLoader} role="status">
                        <IconSpinner aria-hidden="true" />
                    </div>
                )}

                <table className={styles.table}>
                    <colgroup>
                        <col style={{ width: '38px' }} />
                        <col style={{ width: '230px' }} />
                        <col style={{ width: '86px' }} />
                        <col style={{ width: '100px' }} />
                        {/* Цена товара за ед. из заявки (request_price) */}
                        <col style={{ width: '96px' }} />
                        <col style={{ width: '104px' }} />
                        <col style={{ width: '74px' }} />
                        <col style={{ width: '84px' }} />
                        <col style={{ width: '140px' }} />
                        <col style={{ width: '140px' }} />
                        <col style={{ width: '32px' }} />
                        <col style={{ width: '38px' }} />
                        <col style={{ width: '92px' }} />
                    </colgroup>

                    <thead>
                        <tr>
                            <th />
                            <th>Наименование</th>
                            <th>Кол-во</th>
                            <th>Регион</th>
                            <th>Цена за ед., ₽</th>
                            <th>Цена, ₽</th>
                            <th className={styles.center}>Аналоги</th>
                            <th className={styles.center}>Логистика</th>
                            <th>Лучшая стоимость</th>
                            <th>Средняя стоимость</th>
                            <th />
                            <th />
                            <th />
                        </tr>
                    </thead>

                    <tbody>
                        {visible.map((position, index) => (
                            <Fragment key={position.id}>
                                <PositionRow
                                    position={position}
                                    index={(safePage - 1) * perPage + index + 1}
                                    projectRegion={projectRegion}
                                    duplicateOf={position.isDuplicate ? originalIndex(position) : 0}
                                    dirty={dirtyIds.has(position.id)}
                                    showRestart={!bulkDirtyIds.has(position.id)}
                                    analogsLogistics={analogsLogistics[position.id]}
                                    onRestart={() => {
                                        setOpenRows((prev) => new Set(prev).add(position.id));
                                        onRestartPosition(position.id);
                                    }}
                                    onDiscard={() => onDiscardPosition(position.id)}
                                    onDimensions={() => onShowDimensions(position.id)}
                                    open={openRows.has(position.id)}
                                    onOpen={() => toggleRow(position)}
                                    onToggle={(key) => onTogglePosition(position.id, key)}
                                    onChange={(patch) => onChangePosition(position.id, patch)}
                                    onEdit={() => onEditPosition(position)}
                                    onDelete={() => onDeletePosition(position)}
                                />

                                {isSearchStarted(position) && openRows.has(position.id) && (
                                    <tr>
                                        <td colSpan={TABLE_COLUMNS} className={styles.detailsCell}>
                                            <PositionDetails
                                                reportId={reportId}
                                                position={position}
                                                address={deliveryAddress}
                                                onAnalogsLogistics={(status) =>
                                                    handleAnalogsLogistics(position.id, status)
                                                }
                                                onOfferDeleted={() => onOfferDeleted?.(position.id)}
                                            />
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className={styles.foot}>
                {showTotal ? (
                    <span className={styles.total}>
                        <span className={styles.totalCap}>Итого по проекту</span>
                        <b>{formatRub(goodsTotal)}</b>
                        <span className={styles.totalParts}>
                            {deliveryPending
                                ? 'логистика считается…'
                                : deliveryShown
                                    ? `доставка от ${formatRub(deliveryShown)}`
                                    : 'без логистики'}
                            {withoutOffers > 0 && (
                                <span className={styles.totalNote}>
                                    не учтено{' '}
                                    {pluralWithCount(
                                        withoutOffers,
                                        'позиция',
                                        'позиции',
                                        'позиций',
                                    )}{' '}
                                    без предложений
                                </span>
                            )}
                        </span>
                    </span>
                ) : (
                    <span className={styles.footNote}>{footNote}</span>
                )}

                {total > perPage && (
                    <TableFooter
                        className={styles.pager}
                        page={safePage}
                        totalPages={pageCount}
                        rangeLabel={`${(safePage - 1) * perPage + 1}–${Math.min(safePage * perPage, total)} из ${formatNumber(total)}`}
                        onPageChange={goToPage}
                    />
                )}

                <div className={styles.footRight}>
                    {showDirtyActions && (
                        <>
                            <Button size="sm" disabled={restartingDirty} onClick={onDiscardDirty}>
                                Отменить изменения
                            </Button>
                            <Button
                                variant="primary"
                                disabled={
                                    restartingDirty || actionBusy || searchStarting || restartBlocked
                                }
                                onClick={onRestartDirty}
                            >
                                {restartingDirty
                                    ? 'Перезапускаем…'
                                    : dirtyCount === 1
                                        ? 'Перезапустить позицию'
                                        : `Перезапустить ${pluralWithCount(dirtyCount, 'позицию', 'позиции', 'позиций')}`}
                            </Button>
                        </>
                    )}

                    {hasResults && (
                        <Button
                            size="sm"
                            icon={<IconDownload />}
                            onClick={() => onDownload(hasLogistics)}
                        >
                            Скачать отчёт
                        </Button>
                    )}

                    {showsRunLabel && (
                        <Button onClick={onClearAll}>
                            Очистить всё
                        </Button>
                    )}

                    {isSearching ? (
                        <Button variant="danger" disabled={stopping || actionBusy} onClick={onStopSearch}>
                            {stopping ? 'Останавливаем…' : 'Остановить поиск'}
                        </Button>
                    ) : (
                        <Tooltip
                            text={
                                isRestart && branchesBusy
                                    ? 'Дождитесь окончания поиска — ещё ищем аналоги и предложения площадок'
                                    : ''
                            }
                            align="right"
                        >
                            <Button
                                variant={mainIsPrimary ? 'primary' : undefined}
                                icon={<IconSearch />}
                                disabled={
                                    actionBusy ||
                                    restartingDirty ||
                                    (isRestart && (!hasResults || restartBlocked))
                                }
                                onClick={canRunSearch ? onRunSearch : onRestartSearch}
                            >
                                {searchStarting
                                    ? 'Запускаем…'
                                    : hasResults && !canRunSearch
                                        ? 'Искать заново'
                                        : 'Запустить в работу'}
                            </Button>
                        </Tooltip>
                    )}
                </div>
            </div>
        </section>
    );
};